


import bgMap from '@/assets/bg_map.png'
import bg1 from '@/assets/bg1.png'
import bg2 from '@/assets/bg2.png'
import bg3 from '@/assets/bg3.png'
import bg4 from '@/assets/bg4.png'

/**
 * 添加地图贴图
 * @param {Object} mapInstance - 地图实例
 * @param {Object} districtBounds - 行政区边界范围（TMap.LatLngBounds）
 * @param {string} imageSrc - 图片路径（可选，默认使用 bgMap）
 * @param {Object} oldImageLayer - 旧的图片图层实例（可选，用于销毁）
 * @returns {Object|null} 返回新的图片图层实例，失败返回 null
 */
export function addMapImageOverlay(mapInstance, districtBounds, imageSrc = null, oldImageLayer = null) {
  if (!mapInstance || !districtBounds) {
    return null
  }
  
  try {
    // 获取边界的西南角和东北角
    const imageSW = districtBounds.getSouthWest()
    const imageNE = districtBounds.getNorthEast()
    
    // 创建边界框
    const imageLatLngBounds = new TMap.LatLngBounds(imageSW, imageNE)
    
    // 如果已经存在图片图层，先销毁
    if (oldImageLayer) {
      try {
        if (oldImageLayer.destroy && typeof oldImageLayer.destroy === 'function') {
          oldImageLayer.destroy()
        }
      } catch (e) {
        // 忽略错误
      }
    }
    
    // 使用 ImageGroundLayer 添加图片贴图（参考官方示例）
    if (typeof TMap !== 'undefined' && TMap.ImageGroundLayer) {
      const imageUrl = imageSrc || bgMap
      // const imageUrl = "https://lbs-nfncb.nfncb.cn/ueditor/BFVNLqLfj7BZfHnd9BD5OW0A7Vh2jdSAuu1Ff8b3.png"
      const imageLayer = new TMap.ImageGroundLayer({
        bounds: imageLatLngBounds,
        src: imageUrl,
        map: mapInstance,
        minZoom: 8,
        maxZoom: 12,
      })
      return imageLayer
    }
    
    return null
  } catch (error) {
    // 静默处理错误
    return null
  }
}

/**
 * 销毁图片图层
 * @param {Object} imageLayer - 图片图层实例
 */
export function destroyImageLayer(imageLayer) {
  if (!imageLayer) {
    return
  }
  
  try {
    if (imageLayer.destroy && typeof imageLayer.destroy === 'function') {
      imageLayer.destroy()
    }
  } catch (e) {
    // 忽略错误
  }
}

/**
 * 由多边形顶点坐标数组计算能完整呈现该多边形的最小矩形范围
 * @param {Array} latLngList - 坐标点数组
 * @returns {Object|null} 返回 TMap.LatLngBounds 对象，失败返回 null
 */
export function fitBounds(latLngList) {
  if (!latLngList || latLngList.length === 0) {
    return null
  }
  
  let boundsN = latLngList[0].getLat()
  let boundsS = boundsN
  let boundsW = latLngList[0].getLng()
  let boundsE = boundsW
  
  latLngList.forEach((point) => {
    const lat = point.getLat()
    const lng = point.getLng()
    if (lat > boundsN) boundsN = lat
    if (lat < boundsS) boundsS = lat
    if (lng > boundsE) boundsE = lng
    if (lng < boundsW) boundsW = lng
  })
  
  return new TMap.LatLngBounds(
    new TMap.LatLng(boundsS, boundsW),
    new TMap.LatLng(boundsN, boundsE)
  )
}

/**
 * 获取行政区边界
 * @param {Object} mapInstance - 地图实例
 * @param {string} keyword - 行政区关键词（如 '440100' 或 '广州市'）
 * @param {Function} onSuccess - 成功回调函数，参数为 (bounds, shouldFitBounds)
 * @param {Function} onError - 失败回调函数
 */
export function loadDistrictBoundary(mapInstance, keyword = '440100', onSuccess, onError) {
  if (!mapInstance) {
    if (onError) onError(new Error('地图实例不存在'))
    return
  }
  
  if (typeof TMap === 'undefined' || !TMap.service || !TMap.service.District) {
    if (onError) onError(new Error('行政区划服务不可用'))
    useFallbackDistrictData(mapInstance, onSuccess, onError)
    return
  }
  
  try {
    // 创建行政区划服务实例
    const districtService = new TMap.service.District({
      polygon: 1 // 返回行政区划边界
    })
    
    // 获取行政区边界
    districtService
      .search({ keyword: keyword })
      .then((result) => {
        if (result && result.result && result.result.length > 0) {
          // 用于计算所有边界的最东、最西、最南、最北坐标
          const allBounds = []
          
          result.result.forEach((level) => {
            level.forEach((place) => {
              if (place.polygon && place.polygon.length > 0) {
                place.polygon.forEach((polygon) => {
                  const polygonBounds = fitBounds(polygon)
                  if (polygonBounds) {
                    allBounds.push(polygonBounds)
                  }
                })
              }
            })
          })
          
          // 计算所有边界的最东、最西、最南、最北坐标
          if (allBounds.length > 0) {
            const finalBounds = allBounds.reduce((a, b) => {
              if (!a) return b
              if (!b) return a
              return fitBounds([
                a.getNorthEast(),
                a.getSouthWest(),
                b.getNorthEast(),
                b.getSouthWest()
              ])
            })
            
            if (finalBounds && onSuccess) {
              onSuccess(finalBounds, true)
            }
          } else {
            if (onError) onError(new Error('未找到边界数据'))
          }
        } else {
          if (onError) onError(new Error('未找到边界数据'))
        }
      })
      .catch((error) => {
        // 请求失败，使用备用静态数据
        useFallbackDistrictData(mapInstance, onSuccess, onError)
      })
  } catch (error) {
    // 静默处理错误，尝试使用备用数据
    useFallbackDistrictData(mapInstance, onSuccess, onError)
  }
}

/**
 * 使用备用静态数据获取行政区边界
 * @param {Object} mapInstance - 地图实例
 * @param {Function} onSuccess - 成功回调函数，参数为 (bounds, shouldFitBounds)
 * @param {Function} onError - 失败回调函数
 */
export function useFallbackDistrictData(mapInstance, onSuccess, onError) {
  if (!mapInstance) {
    if (onError) onError(new Error('地图实例不存在'))
    return
  }
  
  try {
    // 备用静态数据（来自API返回的格式）
    const fallbackData = {
      result: [[{
        id: "440100",
        name: "广州",
        fullname: "广州市",
        polygon: [
          [113.633258, 22.723414, 113.700606, 22.570287, 113.585288, 22.717514],
          [113.365155, 22.772601, 113.467908, 22.728512, 113.634067, 22.557137, 113.669463, 22.558946, 113.649269, 22.63663, 113.550668, 22.742577, 113.624477, 22.750602, 113.603877, 22.810884, 113.571049, 22.858881, 113.571095, 22.86588, 113.541793, 22.959386, 113.539972, 22.96172, 113.556292, 23.081237, 113.729098, 23.142051, 113.844691, 23.125617, 113.902286, 23.173262, 113.893668, 23.342247, 113.996696, 23.297458, 114.000453, 23.394166, 113.952811, 23.442892, 113.981705, 23.472151, 113.871375, 23.541313, 113.818783, 23.656168, 114.059269, 23.777421, 114.036211, 23.901767, 114.018143, 23.923621, 114.017559, 23.925715, 113.906842, 23.926577, 113.903204, 23.929328, 113.800248, 23.902587, 113.778024, 23.88379, 113.77178, 23.874056, 113.758171, 23.857503, 113.755296, 23.857338, 113.638605, 23.812556, 113.637572, 23.807419, 113.615428, 23.787323, 113.618483, 23.785843, 113.631643, 23.722656, 113.630251, 23.71768, 113.628095, 23.716364, 113.630385, 23.708921, 113.631014, 23.702752, 113.622615, 23.699421, 113.612329, 23.68737, 113.612554, 23.68338, 113.569614, 23.686342, 113.569075, 23.688974, 113.532065, 23.689138, 113.526167, 23.685852, 113.513784, 23.682105, 113.507002, 23.684367, 113.503992, 23.683832, 113.495728, 23.682269, 113.481085, 23.684079, 113.468734, 23.69103, 113.408951, 23.723807, 113.401629, 23.724177, 113.383349, 23.729811, 113.38007, 23.730839, 113.369335, 23.709086, 113.365248, 23.709209, 113.328821, 23.655777, 113.327518, 23.654008, 113.29226, 23.639895, 113.296347, 23.638702, 113.244424, 23.586929, 113.240606, 23.590181, 113.202473, 23.569227, 113.200766, 23.561899, 113.210962, 23.544647, 113.212399, 23.5416, 113.206111, 23.5388, 113.199958, 23.532006, 113.176377, 23.513267, 113.17211, 23.512402, 113.015938, 23.460621, 113.008616, 23.463546, 112.979286, 23.465153, 112.975219, 23.463587, 112.975153, 23.463605, 112.986531, 23.357589, 113.043504, 23.351851, 113.044758, 23.250961, 113.127985, 23.314549, 113.176529, 23.273598, 113.21672, 23.099025, 113.177917, 23.068027, 113.257804, 23.046774, 113.312096, 22.830393, 113.393427, 22.809846]
        ]
      }]]
    }
    
    // 处理备用数据，转换为 TMap.LatLng 对象数组
    const allBounds = []
    
    fallbackData.result.forEach((level) => {
      level.forEach((place) => {
        if (place.polygon && place.polygon.length > 0) {
          place.polygon.forEach((polygonArray) => {
            // polygonArray 是 [lng, lat, lng, lat, ...] 格式的数组
            // 需要转换为 TMap.LatLng 对象数组
            const polygon = []
            for (let i = 0; i < polygonArray.length; i += 2) {
              if (i + 1 < polygonArray.length) {
                const lng = polygonArray[i]
                const lat = polygonArray[i + 1]
                polygon.push(new TMap.LatLng(lat, lng))
              }
            }
            
            if (polygon.length > 0) {
              const polygonBounds = fitBounds(polygon)
              if (polygonBounds) {
                allBounds.push(polygonBounds)
              }
            }
          })
        }
      })
    })
    
    // 计算所有边界的最东、最西、最南、最北坐标
    if (allBounds.length > 0) {
      const finalBounds = allBounds.reduce((a, b) => {
        if (!a) return b
        if (!b) return a
        return fitBounds([
          a.getNorthEast(),
          a.getSouthWest(),
          b.getNorthEast(),
          b.getSouthWest()
        ])
      })
      
      if (finalBounds && onSuccess) {
        onSuccess(finalBounds, true)
      } else {
        if (onError) onError(new Error('计算边界失败'))
      }
    } else {
      if (onError) onError(new Error('未找到边界数据'))
    }
  } catch (error) {
    if (onError) onError(error)
  }
}

